import { Component, OnInit, inject, ViewChild, ElementRef, AfterViewInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { WhatsAppAccountService, WhatsAppBusinessProfile, WhatsAppPhoneNumber } from '../../core/services/whatsapp-account.service';
import { OrganizationService } from '../../core/services/organization.service';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ONBOARDING_STEPS } from '../../core/config/onboarding-steps';
import { environment } from '../../../environments/environment';

// Declare google for TypeScript
declare var google: any;

@Component({
    selector: 'app-business-details-setup',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './business-details-setup.component.html',
    styleUrls: ['./business-details-setup.component.scss']
})
export class BusinessDetailsSetupComponent implements OnInit, AfterViewInit {

    @ViewChild('addressInput') addressInput!: ElementRef;

    organizationId: string = '';
    organizationName: string = '';
    userEmail: string = '';
    isLoading: boolean = false;
    isSaving: boolean = false;
    whatsappAccount: any = null;
    businessProfile: WhatsAppBusinessProfile | null = null;
    profileForm!: FormGroup;
    selectedFile: File | null = null;
    previewUrl: string | null = null;
    phoneNumberId: string = '';
    autocomplete: any = null;
    private googleMapsScriptLoaded: boolean = false;

    // Multi-phone cycling
    allPhones: WhatsAppPhoneNumber[] = [];
    currentPhoneIndex: number = 0;
    completedPhoneIds: Set<string> = new Set();
    private organization: any = null;

    get currentPhone(): WhatsAppPhoneNumber | null {
        return this.allPhones[this.currentPhoneIndex] || null;
    }

    get isLastPhone(): boolean {
        return this.currentPhoneIndex >= this.allPhones.length - 1;
    }

    get hasMultiplePhones(): boolean {
        return this.allPhones.length > 1;
    }

    // Step progress tracking
    currentStep: number = 3;
    totalSteps: number = ONBOARDING_STEPS.length;
    progressPercentage: number = 0;

    // Industry/vertical options
    verticalOptions = [
        { value: '', label: 'Select Industry' },
        { value: 'AUTO', label: 'Automotive' },
        { value: 'BEAUTY', label: 'Beauty, Spa & Salon' },
        { value: 'APPAREL', label: 'Clothing & Apparel' },
        { value: 'EDU', label: 'Education' },
        { value: 'ENTERTAIN', label: 'Entertainment' },
        { value: 'EVENT_PLAN', label: 'Event Planning' },
        { value: 'FINANCE', label: 'Finance' },
        { value: 'GROCERY', label: 'Grocery' },
        { value: 'GOVT', label: 'Government' },
        { value: 'HOTEL', label: 'Hotel & Lodging' },
        { value: 'HEALTH', label: 'Health & Medical' },
        { value: 'NONPROFIT', label: 'Non-profit' },
        { value: 'PROF_SERVICES', label: 'Professional Services' },
        { value: 'RETAIL', label: 'Retail' },
        { value: 'TRAVEL', label: 'Travel & Tourism' },
        { value: 'RESTAURANT', label: 'Restaurant' },
        { value: 'OTHER', label: 'Other' }
    ];

    private auth = inject(Auth);
    private authState$ = authState(this.auth);
    private firestore = inject(Firestore);
    private fb = inject(FormBuilder);
    private whatsappService = inject(WhatsAppAccountService);
    private organizationService = inject(OrganizationService);
    private onboardingService = inject(OnboardingService);

    constructor(private router: Router) {
        this.initializeForm();
    }

    async ngOnInit(): Promise<void> {
        const currentUser = await firstValueFrom(this.authState$);
        if (currentUser) {
            // From Firestore, not Auth: a federated sign-in can leave
            // `user.email` unset with the address only on the provider entry,
            // and the user document already holds the resolved value.
            try {
                const userDocRef = doc(this.firestore, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    this.userEmail = userData['email'] || currentUser.email || '';
                    console.log('User email from Firestore:', this.userEmail, '(from userData:', userData['email'], ', from auth:', currentUser.email, ')');
                } else {
                    this.userEmail = currentUser.email || '';
                    console.log('User document not found, using auth email:', this.userEmail);
                }
            } catch (error) {
                console.error('Error reading user document:', error);
                // Fallback to auth email if Firestore read fails
                this.userEmail = currentUser.email || '';
                console.log('User email from Auth (fallback):', this.userEmail);
            }

            // Pre-fill email immediately
            if (this.userEmail) {
                this.profileForm.patchValue({ email: this.userEmail });
                console.log('Email field pre-filled with:', this.userEmail);
            } else {
                console.log('No email available for pre-filling');
            }

            // Membership comes from the user document, not the Planning Center
            // sign-in claim: a standalone organisation has no such claim.
            const setup = await this.onboardingService.getSetupContext(
                currentUser.uid,
                'business-details-setup'
            );
            this.organizationId = setup.organizationId;
            this.organizationName = setup.organizationName;
            this.currentStep = setup.currentStep;
            this.totalSteps = setup.totalSteps;
            this.progressPercentage = setup.progressPercentage;
        }


        if (this.organizationId) {
            await this.loadWhatsAppAccountAndProfile();
        }
    }

    initializeForm(): void {
        this.profileForm = this.fb.group({
            about: ['', [Validators.maxLength(139)]],
            description: ['', [Validators.maxLength(512)]],
            vertical: [''],
            address: ['', [Validators.maxLength(256)]],
            email: ['', [Validators.email, Validators.maxLength(128)]],
            website1: ['', [Validators.maxLength(256)]],
            website2: ['', [Validators.maxLength(256)]]
        });
    }
    ngAfterViewInit(): void {
        // Load Google Maps API dynamically, then initialize autocomplete
        this.loadGoogleMapsScript().then(() => {
            this.initializeAutocomplete();
        });
    }

    /**
     * Dynamically load Google Maps JavaScript API with Places library
     * Uses environment-specific API key
     */
    private loadGoogleMapsScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (this.googleMapsScriptLoaded || (typeof google !== 'undefined' && google.maps && google.maps.places)) {
                this.googleMapsScriptLoaded = true;
                resolve();
                return;
            }

            // Check if script is already being loaded
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                // Wait for it to load
                existingScript.addEventListener('load', () => {
                    this.googleMapsScriptLoaded = true;
                    resolve();
                });
                existingScript.addEventListener('error', reject);
                return;
            }

            // Create and load the script with extended library for PlaceAutocompleteElement
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googlePlacesApiKey}&libraries=places&loading=async&solution_channel=GMP_QB_addressselection_v1_cABC`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                this.googleMapsScriptLoaded = true;
                resolve();
            };

            script.onerror = (error) => {
                console.error('Failed to load Google Maps API:', error);
                reject(error);
            };

            document.head.appendChild(script);
        });
    }

    initializeAutocomplete(): void {
        // Check if the address input element exists in the DOM
        if (!this.addressInput || !this.addressInput.nativeElement) {
            console.warn('Place autocomplete element not available yet, retrying...');
            setTimeout(() => this.initializeAutocomplete(), 100);
            return;
        }

        // Check if Google Maps API is loaded
        if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
            console.warn('Google Maps API not loaded yet, retrying...');
            setTimeout(() => this.initializeAutocomplete(), 500);
            return;
        }

        try {
            const autocompleteElement = this.addressInput.nativeElement;

            // Listen for place selection using the new gmp-placeselect event
            autocompleteElement.addEventListener('gmp-placeselect', async (event: any) => {
                const place = event.detail.place;

                if (place) {
                    await place.fetchFields({
                        fields: ['displayName', 'formattedAddress']
                    });

                    const address = place.formattedAddress || place.displayName || '';

                    if (address) {
                        this.profileForm.patchValue({ address });
                    }
                }
            });

            // Sync any already-loaded address value into the web component's visible input
            this.syncAddressToElement();

            console.log('PlaceAutocompleteElement initialized successfully');
        } catch (error) {
            console.error('Error initializing PlaceAutocompleteElement:', error);
        }
    }

    private syncAddressToElement(): void {
        const address = this.profileForm.get('address')?.value;
        if (address && this.addressInput?.nativeElement) {
            this.addressInput.nativeElement.value = address;
        }
    }
    async loadWhatsAppAccountAndProfile(): Promise<void> {
        this.isLoading = true;

        try {
            // Load organization data to get contactWebsite
            this.organization = await firstValueFrom(this.organizationService.getOrganization(this.organizationId));

            // Get the WhatsApp account to find all phone numbers
            const account = await firstValueFrom(this.whatsappService.getWhatsAppAccount(this.organizationId));
            this.whatsappAccount = account;

            if (account && account.phoneNumbers && account.phoneNumbers.length > 0) {
                this.allPhones = account.phoneNumbers;
                this.currentPhoneIndex = 0;
                await this.loadProfileForCurrentPhone();
            } else {
                // No WhatsApp account connected yet - pre-fill with organization and user data
                const patchData: any = {};
                if (this.userEmail) patchData.email = this.userEmail;
                if (this.organization?.contactWebsite) patchData.website1 = this.organization.contactWebsite;

                if (Object.keys(patchData).length > 0) {
                    this.profileForm.patchValue(patchData);
                }

                this.isLoading = false;
                setTimeout(() => this.initializeAutocomplete(), 100);
            }
        } catch (error) {
            console.error('Error loading WhatsApp account:', error);
            this.isLoading = false;
            setTimeout(() => this.initializeAutocomplete(), 100);
        }
    }

    async loadProfileForCurrentPhone(): Promise<void> {
        const phone = this.currentPhone;
        if (!phone) return;

        this.isLoading = true;
        this.phoneNumberId = phone.id;
        this.businessProfile = null;
        this.selectedFile = null;
        this.previewUrl = null;
        this.profileForm.reset();

        try {
            const profile = await firstValueFrom(
                this.whatsappService.getBusinessProfile(phone.id, this.organizationId)
            );
            this.businessProfile = profile;
            this.previewUrl = profile.profile_picture_url || null;

            // Pre-populate the form with existing data
            const website1Value = profile.websites?.[0] || this.organization?.contactWebsite || '';
            const patchData: any = {
                about: profile.about || '',
                description: profile.description || '',
                vertical: profile.vertical || '',
                address: profile.address || '',
                website1: website1Value,
                website2: profile.websites?.[1] || ''
            };

            const emailValue = profile.email || this.userEmail;
            if (emailValue) {
                patchData.email = emailValue;
            }

            this.profileForm.patchValue(patchData);
            // Sync address into the PlaceAutocompleteElement visible input
            setTimeout(() => this.syncAddressToElement(), 0);
        } catch (error) {
            console.error('Error loading business profile for phone:', phone.id, error);

            // Fall back to organization/user defaults
            const patchData: any = {};
            if (this.userEmail) patchData.email = this.userEmail;
            if (this.organization?.contactWebsite) patchData.website1 = this.organization.contactWebsite;

            if (Object.keys(patchData).length > 0) {
                this.profileForm.patchValue(patchData);
            }
        } finally {
            this.isLoading = false;
            setTimeout(() => this.initializeAutocomplete(), 100);
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const file = input.files[0];

            // Validate file type
            if (!file.type.match(/image\/(jpeg|png)/)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please select a JPEG or PNG image.',
                    confirmButtonColor: '#556ee6'
                });
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Profile picture must be less than 5MB.',
                    confirmButtonColor: '#556ee6'
                });
                return;
            }

            this.selectedFile = file;

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.previewUrl = e.target?.result as string;
            };
            reader.readAsDataURL(file);
        }
    }

    async saveAndContinue(): Promise<void> {
        if (this.profileForm.invalid) {
            Object.keys(this.profileForm.controls).forEach(key => {
                this.profileForm.get(key)?.markAsTouched();
            });
            return;
        }

        // Validate URLs if provided
        const website1 = this.profileForm.value.website1?.trim();
        const website2 = this.profileForm.value.website2?.trim();

        for (const url of [website1, website2]) {
            if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Invalid URL',
                    text: 'Website URLs must start with http:// or https://',
                    confirmButtonColor: '#556ee6'
                });
                return;
            }
        }

        // Check if we have a phone number to update
        if (!this.phoneNumberId) {
            await Swal.fire({
                icon: 'info',
                title: 'WhatsApp Not Connected',
                text: 'Please connect your WhatsApp account first to update business details.',
                confirmButtonColor: '#556ee6'
            });
            await this.skip();
            return;
        }

        this.isSaving = true;

        try {
            // Build profile data
            const websites: string[] = [];
            if (website1) websites.push(website1);
            if (website2) websites.push(website2);

            // Keys are omitted rather than set to undefined. The callable
            // client encodes `undefined` as an explicit null on the wire, and
            // the server has to tell "leave this alone" from "set this to
            // nothing" — a null said neither, and used to crash the call.
            const profileData: Record<string, unknown> = {};
            const addIfSet = (key: string, value: unknown) => {
                if (value !== null && value !== undefined && value !== '') {
                    profileData[key] = value;
                }
            };

            addIfSet('about', this.profileForm.value.about);
            addIfSet('description', this.profileForm.value.description);
            addIfSet('vertical', this.profileForm.value.vertical);
            addIfSet('address', this.profileForm.value.address);
            addIfSet('email', this.profileForm.value.email);
            if (websites.length > 0) {
                profileData['websites'] = websites;
            }

            // Update business profile
            await firstValueFrom(
                this.whatsappService.updateBusinessProfile(this.phoneNumberId, this.organizationId, profileData)
            );

            // Upload profile picture if selected
            if (this.selectedFile) {
                await this.uploadProfilePicture();
            }

            // Mark this phone as completed
            this.completedPhoneIds.add(this.phoneNumberId);

            // Check if there are more phones to configure
            if (!this.isLastPhone) {
                this.isSaving = false;
                this.currentPhoneIndex++;

                const toast = Swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                toast.fire({
                    icon: 'success',
                    title: `Profile saved for ${this.allPhones[this.currentPhoneIndex - 1].displayPhoneNumber}`
                });

                await this.loadProfileForCurrentPhone();
            } else {
                await this.completeStepAndNavigate();
            }
        } catch (error: any) {
            console.error('Error saving business profile:', error);
            this.isSaving = false;

            await Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: error.message || 'Failed to update business profile. Please try again.',
                confirmButtonColor: '#556ee6'
            });
        }
    }

    private async uploadProfilePicture(): Promise<void> {
        if (!this.selectedFile) return;

        return new Promise<void>((resolve) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const base64Image = (e.target?.result as string).split(',')[1];
                const mimeType = this.selectedFile!.type;

                try {
                    await firstValueFrom(
                        this.whatsappService.updateProfilePicture(this.phoneNumberId, this.organizationId, base64Image, mimeType)
                    );
                } catch (error) {
                    console.error('Error uploading profile picture:', error);
                }
                resolve();
            };
            reader.readAsDataURL(this.selectedFile!);
        });
    }

    async completeStepAndNavigate(): Promise<void> {
        try {
            // Mark step as complete
            await this.onboardingService.completeStep(this.organizationId, 'business-details-setup');

            // Get next step
            const nextStep = await this.onboardingService.resolveNextStep(this.organizationId);

            this.isSaving = false;

            // Navigate to next step or dashboard
            if (nextStep) {
                await this.router.navigate([nextStep.route]);
            } else {
                await this.router.navigate(['/']);
            }
        } catch (error) {
            console.error('Error completing onboarding step:', error);
            this.isSaving = false;
        }
    }

    async skip(): Promise<void> {
        try {
            // Mark step as complete (even though skipped)
            await this.onboardingService.completeStep(this.organizationId, 'business-details-setup', 'skipped');

            // Get next step
            const nextStep = await this.onboardingService.resolveNextStep(this.organizationId);

            // Navigate to next step or dashboard
            if (nextStep) {
                await this.router.navigate([nextStep.route]);
            } else {
                await this.router.navigate(['/']);
            }
        } catch (error) {
            console.error('Error skipping step:', error);
        }
    }

    getRemainingChars(fieldName: string, maxLength: number): number {
        const value = this.profileForm.get(fieldName)?.value || '';
        return maxLength - value.length;
    }
}
